import { computeMissionTotals, type TimeSlot } from '@avity/engine'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useSearch } from '@tanstack/react-router'
import { CheckCircle2, CloudOff, Lock, Send } from 'lucide-react'
import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react'

import { AppShell } from '@/components/layout/app-shell'
import { PhotoCapture } from '@/components/mission/photo-capture'
import { SignaturePad } from '@/components/mission/signature-pad'
import { TimeSlots, type TimeQuad } from '@/components/mission/time-slots'
import { TotalsDisplay } from '@/components/mission/totals-display'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { SelectSearch, type SelectSearchOption } from '@/components/ui/select-search'
import { Spinner } from '@/components/ui/spinner'
import { useApi } from '@/hooks/use-api'
import { useAuth } from '@/hooks/use-auth'
import { useSyncStatus } from '@/hooks/use-sync-status'
import { ApiError } from '@/lib/api'
import { triggerSync } from '@/lib/offline/sync-controller'
import { enqueueMission } from '@/lib/offline/sync-queue'
import { type PendingMission, type PendingMissionPayload } from '@/lib/offline/types'
import { uuidv7 } from '@/lib/uuid'

/**
 * Mission form — the page drivers use 5 to 15 times a day.
 *
 * Design intent
 * -------------
 * One long scrollable form, NOT a 4-step wizard. A wizard adds
 * "Next" friction the V1 PHP page didn't have, and drivers
 * complete this form in under a minute when nothing's broken.
 * Sections are visually separated by `Card` containers so the
 * eye groups them, but the page never asks the user to commit
 * to a step.
 *
 * Form sections (top to bottom):
 *   1. Infos mission (site, date, client, vehicle, isDaily)
 *   2. Créneaux horaires (4 slots, real-time totals via engine)
 *   3. Bonus (lavage, déplacement, péage)
 *   4. Signatures + photo (all optional)
 *   5. Totaux temps réel + bouton Envoyer (sticky bottom)
 *
 * Submission flow
 * ---------------
 * 1. Client-side validation: site, date, ≥1 complete slot.
 *    On error: scroll to first invalid field + highlight.
 * 2. POST /api/missions → mission id.
 * 3. If signatures present → POST /:id/signatures (FormData).
 * 4. If photo present → POST /:id/receipt-photo (FormData).
 * 5. Mark dashboard cache stale, navigate back to /dashboard.
 *
 * Steps 3 & 4 failures don't roll back the mission — the row is
 * already in the database, and the driver can reupload from the
 * dashboard later (passe 3 will queue them offline). We surface
 * the partial-failure state so the driver knows.
 */

/* -------------------------------------------------------------------------- */
/*  Types — narrow shapes of the API responses we actually consume             */
/* -------------------------------------------------------------------------- */

interface ClientLite {
  id: string
  name: string
  email: string | null
}
interface VehicleLite {
  id: string
  registration: string
  brand: string | null
  model: string | null
  type: string | null
  active: boolean
}
interface PlanningResponse {
  id: string
  driverId: string
  vehicleId: string | null
  clientId: string | null
  site: string
  date: string
  isDaily: boolean
}
interface MissionCreatedResponse {
  id: string
}

/* -------------------------------------------------------------------------- */
/*  Page                                                                       */
/* -------------------------------------------------------------------------- */

export function MissionFormPage() {
  const api = useApi()
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  const { user } = useAuth()
  const { isOnline } = useSyncStatus()
  const search = useSearch({ strict: false }) as { planningId?: string }

  /* -------- form state -------- */

  const [site, setSite] = useState('')
  const [date, setDate] = useState(today())
  const [clientId, setClientId] = useState<string | null>(null)
  const [vehicleId, setVehicleId] = useState<string | null>(null)
  const [isDaily, setIsDaily] = useState(false)
  const [slots, setSlots] = useState<TimeQuad>(['', '', '', ''])
  const [washing, setWashing] = useState(false)
  const [travel, setTravel] = useState('')
  const [tollAmount, setTollAmount] = useState('')
  const [supervisorSignature, setSupervisorSignature] = useState<Blob | null>(null)
  const [driverSignature, setDriverSignature] = useState<Blob | null>(null)
  const [photo, setPhoto] = useState<File | null>(null)

  const [errors, setErrors] = useState<FormErrors>({})
  const [submitting, setSubmitting] = useState(false)
  const [submitError, setSubmitError] = useState<string | null>(null)
  const [submitWarning, setSubmitWarning] = useState<string | null>(null)
  const [success, setSuccess] = useState(false)

  // Refs used by the scroll-to-error logic.
  const siteRef = useRef<HTMLInputElement>(null)
  const dateRef = useRef<HTMLInputElement>(null)
  const slotsRef = useRef<HTMLDivElement>(null)

  /* -------- prefill from planning (optional) -------- */

  const planningQuery = useQuery({
    queryKey: ['planning', search.planningId] as const,
    queryFn: () => api.get<PlanningResponse>(`/api/planning/${search.planningId}`),
    enabled: search.planningId !== undefined,
    staleTime: 5 * 60 * 1000,
  })

  // Hydrate form fields once planning loads. Runs whenever the
  // query settles; the `siteLocked` check below means the user
  // can't undo the lock by editing the date or vehicle.
  useEffect(() => {
    if (planningQuery.data === undefined) return
    const p = planningQuery.data
    setSite(p.site)
    setDate(p.date)
    setClientId(p.clientId)
    setVehicleId(p.vehicleId)
    setIsDaily(p.isDaily)
  }, [planningQuery.data])

  const siteLocked = planningQuery.data !== undefined

  /* -------- clients + vehicles for the searchable selects -------- */

  const clientsQuery = useQuery({
    queryKey: ['clients', 'lite'] as const,
    queryFn: () =>
      api.get<ClientLite[]>('/api/clients', { limit: 100, sortBy: 'name', sortOrder: 'asc' }),
    staleTime: 5 * 60 * 1000,
  })
  const vehiclesQuery = useQuery({
    queryKey: ['vehicles', 'lite'] as const,
    queryFn: () =>
      api.get<VehicleLite[]>('/api/vehicles', {
        limit: 100,
        includeInactive: false,
        sortBy: 'registration',
        sortOrder: 'asc',
      }),
    staleTime: 5 * 60 * 1000,
  })

  const clientOptions: SelectSearchOption[] = useMemo(
    () =>
      (clientsQuery.data ?? []).map((c) => {
        const opt: SelectSearchOption = { value: c.id, label: c.name }
        if (c.email !== null && c.email !== '') opt.detail = c.email
        return opt
      }),
    [clientsQuery.data],
  )
  const vehicleOptions: SelectSearchOption[] = useMemo(
    () =>
      (vehiclesQuery.data ?? []).map((v) => {
        const detailParts = [v.brand, v.model, v.type].filter(
          (s): s is string => s !== null && s !== '',
        )
        const opt: SelectSearchOption = { value: v.id, label: v.registration }
        if (detailParts.length > 0) opt.detail = detailParts.join(' · ')
        return opt
      }),
    [vehiclesQuery.data],
  )

  /* -------- live totals via engine -------- */

  const totals = useMemo(
    () =>
      computeMissionTotals({
        slots: quadToSlots(slots),
        washing,
        travel,
      }),
    [slots, washing, travel],
  )

  const hasUsableSlot = quadToSlots(slots).length > 0

  /* -------- create-mission mutation -------- */

  const createMission = useMutation({
    mutationFn: async (): Promise<MissionCreatedResponse> => {
      if (user === null) {
        throw new ApiError('AUTHENTICATION_REQUIRED', 'Session expirée', 401)
      }
      const body = buildMissionPayload({
        driverId: user.id,
        site,
        date,
        clientId,
        vehicleId,
        slots,
        washing,
        travel,
        tollAmount,
        isDaily,
        planningId: search.planningId ?? null,
      })
      return api.post<MissionCreatedResponse>('/api/missions', body)
    },
  })

  /* -------- submit handler -------- */

  async function handleSubmit(event: FormEvent<HTMLFormElement>): Promise<void> {
    event.preventDefault()
    setSubmitError(null)
    setSubmitWarning(null)

    const validation = validate({
      site,
      date,
      slots,
    })
    setErrors(validation)
    if (Object.keys(validation).length > 0) {
      scrollToFirstError(validation, { siteRef, dateRef, slotsRef })
      return
    }

    setSubmitting(true)
    try {
      // Offline path: drop the mission into the IndexedDB queue
      // and let the sync controller pick it up when the network
      // returns. The driver sees the same green-check splash as
      // the online path so the field experience is consistent
      // whether they're in a 5G office or a basement carpark.
      if (!isOnline) {
        await queueOffline()
        setSubmitWarning(
          'Mission enregistrée localement. Elle sera envoyée dès le retour du réseau.',
        )
        finishSuccessfully()
        return
      }

      // Online path — try the live API. If the create itself
      // fails on a network error (DNS, timeout…) we fall back to
      // the queue rather than losing the driver's work. 4xx/5xx
      // responses still surface as errors because they mean the
      // server rejected the payload — retrying the same payload
      // wouldn't help.
      let created: MissionCreatedResponse
      try {
        created = await createMission.mutateAsync()
      } catch (err) {
        if (isLikelyNetworkError(err)) {
          await queueOffline()
          setSubmitWarning(
            'Réseau instable — mission enregistrée localement, envoi automatique dès le retour du réseau.',
          )
          finishSuccessfully()
          return
        }
        throw err
      }

      // Best-effort uploads — failure is reported but doesn't
      // roll back the mission row.
      let uploadFailed = false
      if (supervisorSignature !== null || driverSignature !== null) {
        try {
          await uploadSignatures(api, created.id, {
            supervisor: supervisorSignature,
            driver: driverSignature,
          })
        } catch {
          uploadFailed = true
        }
      }
      if (photo !== null) {
        try {
          await uploadPhoto(api, created.id, photo)
        } catch {
          uploadFailed = true
        }
      }

      // Invalidate the dashboard so the new mission's "Bon rempli"
      // badge shows up next visit, and the dismiss button becomes
      // enabled.
      await queryClient.invalidateQueries({ queryKey: ['planning', 'my-dashboard'] })

      if (uploadFailed) {
        setSubmitWarning(
          "Mission enregistrée, mais certains fichiers n'ont pas pu être envoyés. Vous pourrez réessayer plus tard.",
        )
      }

      finishSuccessfully()
    } catch (err) {
      setSubmitError(
        err instanceof ApiError
          ? err.message
          : "Impossible d'enregistrer la mission. Vérifiez votre connexion.",
      )
    } finally {
      setSubmitting(false)
    }
  }

  function finishSuccessfully(): void {
    setSuccess(true)
    // 1.2 s is enough to register the green check without
    // feeling slow on a fast network.
    window.setTimeout(() => {
      void navigate({ to: '/dashboard' })
    }, 1200)
  }

  async function queueOffline(): Promise<void> {
    if (user === null) {
      throw new ApiError('AUTHENTICATION_REQUIRED', 'Session expirée', 401)
    }
    const payload = buildMissionPayload({
      driverId: user.id,
      site,
      date,
      clientId,
      vehicleId,
      slots,
      washing,
      travel,
      tollAmount,
      isDaily,
      planningId: search.planningId ?? null,
    })
    const pending: PendingMission = {
      localId: uuidv7(),
      payload: payload as PendingMissionPayload,
      signatures: {
        supervisor: supervisorSignature,
        driver: driverSignature,
      },
      receiptPhoto: photo,
      createdAt: Date.now(),
      retryCount: 0,
      lastError: null,
      status: 'pending',
    }
    await enqueueMission(pending)
    // If we re-gain connectivity right after queuing, the
    // controller's online-event listener will fire and run the
    // walker. Calling triggerSync directly here covers the case
    // where the driver was online when they hit submit but a
    // create-time TCP RST counted as "network error" — we want
    // the walker to retry immediately.
    void triggerSync(api)
  }

  /* -------- early returns -------- */

  if (search.planningId !== undefined && planningQuery.isLoading) {
    return (
      <AppShell>
        <div className="py-12 flex justify-center">
          <Spinner size={28} label="Chargement du planning" />
        </div>
      </AppShell>
    )
  }

  if (search.planningId !== undefined && planningQuery.isError) {
    return (
      <AppShell>
        <Card className="p-4 text-danger">
          Planning introuvable. Revenez au tableau de bord.
        </Card>
      </AppShell>
    )
  }

  /* -------- render -------- */

  return (
    <AppShell>
      <form onSubmit={handleSubmit} noValidate className="flex flex-col gap-4 pb-4">
        <header>
          <h1 className="text-2xl font-bold text-text">Nouveau bon</h1>
          {siteLocked ? (
            <p className="mt-1 text-sm text-text-muted">
              Pré-rempli depuis le planning du chantier.
            </p>
          ) : (
            <p className="mt-1 text-sm text-text-muted">
              Mission libre — sans planning associé.
            </p>
          )}
        </header>

        {/* ------- 1. infos ------- */}
        <Card className="p-4 flex flex-col gap-3">
          <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wide">
            Infos mission
          </h2>

          <div className="relative">
            <Input
              ref={siteRef}
              label="Chantier"
              type="text"
              required
              value={site}
              onChange={(e) => setSite(e.target.value)}
              error={errors.site ?? null}
              readOnly={siteLocked}
              {...(siteLocked ? { hint: 'Verrouillé par le planning' } : {})}
            />
            {siteLocked ? (
              <Lock
                size={16}
                aria-hidden="true"
                className="absolute right-3 top-9 text-text-muted pointer-events-none"
              />
            ) : null}
          </div>

          <Input
            ref={dateRef}
            label="Date"
            type="date"
            required
            value={date}
            onChange={(e) => setDate(e.target.value)}
            error={errors.date ?? null}
          />

          <SelectSearch
            label="Client"
            placeholder="Sélectionner un client"
            value={clientId}
            onChange={setClientId}
            options={clientOptions}
            loading={clientsQuery.isLoading}
          />

          <SelectSearch
            label="Véhicule"
            placeholder="Sélectionner un véhicule"
            value={vehicleId}
            onChange={setVehicleId}
            options={vehicleOptions}
            loading={vehiclesQuery.isLoading}
          />

          <label className="flex items-center gap-3 mt-1 min-h-[3rem] cursor-pointer">
            <input
              type="checkbox"
              checked={isDaily}
              onChange={(e) => setIsDaily(e.target.checked)}
              className="size-5 rounded border-2 border-surface-border text-brand focus:ring-brand"
            />
            <span className="text-base">Mission journalière (forfait)</span>
          </label>
        </Card>

        {/* ------- 2. slots ------- */}
        <Card className="p-4 flex flex-col gap-3" ref={slotsRef as never}>
          <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wide">
            Créneaux horaires
          </h2>
          <TimeSlots value={slots} onChange={setSlots} />
          {errors.slots !== undefined ? (
            <p className="text-sm text-danger">{errors.slots}</p>
          ) : null}
        </Card>

        {/* ------- 3. bonus ------- */}
        <Card className="p-4 flex flex-col gap-3">
          <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wide">
            Bonus &amp; extras
          </h2>

          <Input
            label="Déplacement"
            type="text"
            value={travel}
            onChange={(e) => setTravel(e.target.value)}
            placeholder="ex : 1h30, 1.5, 90min"
            hint="Ajouté au total. Format flexible."
          />

          <label className="flex items-center gap-3 min-h-[3rem] cursor-pointer">
            <input
              type="checkbox"
              checked={washing}
              onChange={(e) => setWashing(e.target.checked)}
              className="size-5 rounded border-2 border-surface-border text-brand focus:ring-brand"
            />
            <span className="text-base">Lavage du véhicule (+30 min)</span>
          </label>

          <Input
            label="Péage / OBU (€)"
            type="number"
            inputMode="decimal"
            min="0"
            step="0.01"
            value={tollAmount}
            onChange={(e) => setTollAmount(e.target.value)}
            placeholder="Optionnel"
          />
        </Card>

        {/* ------- 4. signatures + photo ------- */}
        <Card className="p-4 flex flex-col gap-4">
          <h2 className="text-sm font-semibold text-text-muted uppercase tracking-wide">
            Signatures &amp; bon de livraison
          </h2>
          <SignaturePad
            label="Signature chef de chantier"
            onSignatureChange={setSupervisorSignature}
          />
          <SignaturePad
            label="Signature chauffeur"
            onSignatureChange={setDriverSignature}
          />
          <PhotoCapture label="Photo du bon (optionnel)" onPhotoChange={setPhoto} />
        </Card>

        {/* ------- 5. totals + submit (sticky-ish at bottom of form) ------- */}
        <div className="flex flex-col gap-3">
          <TotalsDisplay totals={totals} empty={!hasUsableSlot} />

          {submitError !== null ? (
            <p
              role="alert"
              className="text-sm text-danger text-center font-medium"
            >
              {submitError}
            </p>
          ) : null}
          {submitWarning !== null ? (
            <p
              role="alert"
              className="text-sm text-warning text-center font-medium"
            >
              {submitWarning}
            </p>
          ) : null}

          <Button
            type="submit"
            variant="primary"
            size="lg"
            fullWidth
            loading={submitting}
            disabled={submitting || success}
          >
            {success ? (
              <>
                <CheckCircle2 size={20} />
                {isOnline ? 'Mission envoyée' : 'Mission enregistrée'}
              </>
            ) : submitting ? (
              <Spinner label={isOnline ? 'Envoi en cours' : 'Enregistrement'} />
            ) : isOnline ? (
              <>
                <Send size={18} />
                Envoyer la mission
              </>
            ) : (
              <>
                <CloudOff size={18} />
                Enregistrer (hors connexion)
              </>
            )}
          </Button>
        </div>
      </form>
    </AppShell>
  )
}

/* -------------------------------------------------------------------------- */
/*  Validation                                                                 */
/* -------------------------------------------------------------------------- */

interface FormErrors {
  site?: string
  date?: string
  slots?: string
}

interface ValidateInput {
  site: string
  date: string
  slots: TimeQuad
}

const HH_MM_REGEX = /^([01]\d|2[0-3]):[0-5]\d$/u
const ISO_DATE_REGEX = /^\d{4}-\d{2}-\d{2}$/u

function validate({ site, date, slots }: ValidateInput): FormErrors {
  const errors: FormErrors = {}
  if (site.trim() === '') errors.site = 'Le nom du chantier est obligatoire.'
  if (!ISO_DATE_REGEX.test(date)) errors.date = 'Date obligatoire.'

  // At least one slot must have BOTH start and end as valid HH:MM.
  const hasOneComplete =
    (HH_MM_REGEX.test(slots[0]) && HH_MM_REGEX.test(slots[1])) ||
    (HH_MM_REGEX.test(slots[2]) && HH_MM_REGEX.test(slots[3]))
  if (!hasOneComplete) {
    errors.slots = 'Au moins un créneau complet (début + fin) est obligatoire.'
  }
  return errors
}

interface ScrollRefs {
  siteRef: React.RefObject<HTMLInputElement | null>
  dateRef: React.RefObject<HTMLInputElement | null>
  slotsRef: React.RefObject<HTMLDivElement | null>
}

function scrollToFirstError(errors: FormErrors, refs: ScrollRefs): void {
  // Order matters — site → date → slots.
  const target =
    errors.site !== undefined
      ? refs.siteRef.current
      : errors.date !== undefined
        ? refs.dateRef.current
        : errors.slots !== undefined
          ? refs.slotsRef.current
          : null
  target?.scrollIntoView({ behavior: 'smooth', block: 'center' })
  if (target instanceof HTMLInputElement) {
    target.focus({ preventScroll: true })
  }
}

/* -------------------------------------------------------------------------- */
/*  Payload + upload helpers                                                   */
/* -------------------------------------------------------------------------- */

interface BuildPayloadInput {
  driverId: string
  site: string
  date: string
  clientId: string | null
  vehicleId: string | null
  slots: TimeQuad
  washing: boolean
  travel: string
  tollAmount: string
  isDaily: boolean
  planningId: string | null
}

interface MissionPayload {
  driverId: string
  site: string
  date: string
  clientId: string | null
  vehicleId: string | null
  slots: TimeSlot[]
  washing: boolean
  travel: string
  tollAmount?: number
  planningId?: string
  isDaily: boolean
}

function buildMissionPayload(input: BuildPayloadInput): MissionPayload {
  const payload: MissionPayload = {
    driverId: input.driverId,
    site: input.site.trim(),
    date: input.date,
    clientId: input.clientId,
    vehicleId: input.vehicleId,
    slots: quadToSlots(input.slots),
    washing: input.washing,
    travel: input.travel,
    isDaily: input.isDaily,
  }
  // Only include optional fields when they have a value — the API
  // is `nullish` on these, but `exactOptionalPropertyTypes` rejects
  // `undefined` keys when the JSON.stringify pass would ship them.
  const toll = parseTollAmount(input.tollAmount)
  if (toll !== null) payload.tollAmount = toll
  if (input.planningId !== null) payload.planningId = input.planningId
  return payload
}

function quadToSlots(quad: TimeQuad): TimeSlot[] {
  const [ms, me, as, ae] = quad
  const slots: TimeSlot[] = []
  if (HH_MM_REGEX.test(ms) && HH_MM_REGEX.test(me)) {
    slots.push({ start: ms, end: me })
  }
  if (HH_MM_REGEX.test(as) && HH_MM_REGEX.test(ae)) {
    slots.push({ start: as, end: ae })
  }
  return slots
}

function parseTollAmount(raw: string): number | null {
  const trimmed = raw.trim()
  if (trimmed === '') return null
  // Accept comma as decimal separator — common in fr-FR keyboards.
  const n = Number(trimmed.replace(',', '.'))
  if (!Number.isFinite(n) || n < 0) return null
  return Math.round(n * 100) / 100
}

async function uploadSignatures(
  api: ReturnType<typeof useApi>,
  missionId: string,
  blobs: { supervisor: Blob | null; driver: Blob | null },
): Promise<void> {
  const fd = new FormData()
  if (blobs.supervisor !== null) {
    fd.append('supervisorSignature', blobs.supervisor, 'supervisor.png')
  }
  if (blobs.driver !== null) {
    fd.append('driverSignature', blobs.driver, 'driver.png')
  }
  await api.upload<unknown>(`/api/missions/${missionId}/signatures`, fd)
}

async function uploadPhoto(
  api: ReturnType<typeof useApi>,
  missionId: string,
  file: File,
): Promise<void> {
  const fd = new FormData()
  fd.append('photo', file, file.name)
  await api.upload<unknown>(`/api/missions/${missionId}/receipt-photo`, fd)
}

function today(): string {
  // Local "YYYY-MM-DD" — avoid `toISOString()` which would shift
  // the day for users east of Greenwich at midnight UTC.
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

/**
 * Heuristic: did this throw most likely come from "no network" or
 * from "the API returned 4xx/5xx"? Used to decide whether to fall
 * back on the offline queue.
 *
 * - `ApiError` with `NETWORK_ERROR` code (set by `api.ts` when JSON
 *   parsing fails on what is presumably an empty/HTML response).
 * - `TypeError: Failed to fetch` — the canonical browser error
 *   when the connection couldn't even be opened.
 *
 * Anything with a real HTTP status (400, 422, 500…) means the
 * server *did* answer, which means retrying the same payload
 * would fail again. Surface those as proper errors.
 */
function isLikelyNetworkError(err: unknown): boolean {
  if (err instanceof ApiError) {
    return err.code === 'NETWORK_ERROR'
  }
  if (err instanceof TypeError) {
    return /failed to fetch|network/i.test(err.message)
  }
  return false
}
